import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { illustrationMonitorKeys } from '@/api/waterlily';
import { illustrationMonitorApi } from '@/features/illustration-monitor/api/illustration-monitor-api';
import { MonitorQuotesRequest, MonitorRequestsRequest } from '@/features/illustration-monitor/types';

const TERMINAL_STATUSES = new Set(['completed', 'error']);

export function useMonitorAvailability() {
  return useQuery({
    queryKey: [...illustrationMonitorKeys.all, 'availability'],
    queryFn: () => illustrationMonitorApi.getAvailability(),
    staleTime: 5 * 60_000,
  });
}

export function useMonitorCarriers() {
  return useQuery({
    queryKey: [...illustrationMonitorKeys.all, 'carriers'],
    queryFn: () => illustrationMonitorApi.getCarriers(),
    staleTime: 5 * 60_000,
  });
}

export function useMonitorProducts(insuranceCarrierId: number | null) {
  return useQuery({
    queryKey: [...illustrationMonitorKeys.all, 'products', insuranceCarrierId],
    queryFn: () => illustrationMonitorApi.getProducts(insuranceCarrierId!),
    enabled: !!insuranceCarrierId,
    staleTime: 5 * 60_000,
  });
}

export function useMonitorStates(
  insuranceCarrierId: number | null,
  productNames: string[] | null,
  quoteType: string | null,
) {
  return useQuery({
    queryKey: [...illustrationMonitorKeys.all, 'states', insuranceCarrierId, productNames, quoteType],
    queryFn: () =>
      illustrationMonitorApi.getStates({
        insuranceCarrierId: insuranceCarrierId!,
        productNames: productNames!,
        quoteType: quoteType!,
      }),
    enabled: !!insuranceCarrierId && !!productNames?.length && !!quoteType,
    staleTime: 5 * 60_000,
  });
}

export function useMonitorQuotes(request: MonitorQuotesRequest | null) {
  return useQuery({
    queryKey: illustrationMonitorKeys.list({ request }),
    queryFn: () => illustrationMonitorApi.queryQuotes(request!),
    enabled: !!request,
    staleTime: 30_000,
  });
}

export function useMonitorRequests(request: MonitorRequestsRequest | null) {
  return useQuery({
    queryKey: [...illustrationMonitorKeys.all, 'requests', request],
    queryFn: () => illustrationMonitorApi.queryRequests(request!),
    enabled: !!request,
    staleTime: 10_000,
  });
}

export function useMonitorQuoteById(quoteId: string | null) {
  return useQuery({
    queryKey: [...illustrationMonitorKeys.all, 'quoteById', quoteId],
    queryFn: () => illustrationMonitorApi.getQuoteById(quoteId!),
    enabled: !!quoteId,
  });
}

export function useMonitorRun() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      quoteId,
      quoteType,
      clientState,
    }: {
      quoteId: string;
      quoteType: string;
      clientState?: string;
    }) => illustrationMonitorApi.run(quoteId, quoteType, clientState),
    onSuccess: ({ requestId }) => {
      queryClient.invalidateQueries({ queryKey: illustrationMonitorKeys.detail(requestId) });
      toast.success('Illustration requested', {
        description: 'Generation started. Polling for completion...',
      });
    },
    onError: (err: Error) => {
      toast.error('Failed to start illustration', {
        description: err?.message ?? 'Unknown error',
      });
    },
  });
}

export function useMonitorRunStatus(requestId: string | null) {
  return useQuery({
    queryKey: illustrationMonitorKeys.detail(requestId ?? ''),
    queryFn: () => illustrationMonitorApi.getStatus(requestId!),
    enabled: !!requestId,
    refetchInterval: query => {
      const data = query.state.data;
      if (!data) return 3_000;
      return TERMINAL_STATUSES.has(data.status) ? false : 3_000;
    },
  });
}
